import { X } from 'lucide-react';
import { Dialog as D, Popover as P, Tooltip as T } from 'radix-ui';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { formatKeys } from '@/lib/shortcuts';
import { Kbd } from './misc';

// ---------- Dialog ----------

export const Dialog = D.Root;
export const DialogTrigger = D.Trigger;
export const DialogClose = D.Close;

const DIALOG_SIZES = { sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-2xl', xl: 'max-w-4xl' } as const;

export function DialogContent({
  className,
  children,
  size = 'md',
  hideClose,
  ...props
}: ComponentProps<typeof D.Content> & { size?: keyof typeof DIALOG_SIZES; hideClose?: boolean }) {
  return (
    <D.Portal>
      <D.Overlay className="fixed inset-0 z-50 animate-fade bg-overlay" />
      <D.Content
        className={cn(
          'fixed top-[12vh] left-1/2 z-50 w-[calc(100vw-2rem)] -translate-x-1/2 animate-pop',
          'max-h-[80vh] overflow-y-auto rounded-xl border border-border bg-surface p-5 shadow-pop',
          DIALOG_SIZES[size],
          className,
        )}
        {...props}
      >
        {children}
        {hideClose ? null : (
          <D.Close
            aria-label="Close"
            className="absolute top-3.5 right-3.5 rounded p-1 text-fg-subtle hover:bg-hover hover:text-fg"
          >
            <X className="size-4" />
          </D.Close>
        )}
      </D.Content>
    </D.Portal>
  );
}

export function DialogHeader({ title, description }: { title: ReactNode; description?: ReactNode }) {
  return (
    <div className="mb-4 pr-6">
      <D.Title className="text-base font-semibold text-fg">{title}</D.Title>
      {description ? (
        <D.Description className="mt-1 text-sm text-fg-muted">{description}</D.Description>
      ) : (
        <D.Description className="sr-only">{typeof title === 'string' ? title : 'Dialog'}</D.Description>
      )}
    </div>
  );
}

export function DialogFooter({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('mt-5 flex items-center justify-end gap-2', className)} {...props} />;
}

/** A dialog that fills most of the viewport: a work surface like the task popup, not a form. */
export function WorkspaceDialogContent({
  className,
  children,
  title,
  ...props
}: ComponentProps<typeof D.Content> & { title: string }) {
  return (
    <D.Portal>
      <D.Overlay className="fixed inset-0 z-50 animate-fade bg-overlay" />
      <D.Content
        className={cn(
          'fixed inset-x-0 top-[3vh] left-1/2 z-50 flex h-[94vh] w-[calc(100vw-2rem)] max-w-[90rem] -translate-x-1/2 animate-pop flex-col',
          'overflow-hidden rounded-xl border border-border bg-surface shadow-pop',
          className,
        )}
        {...props}
      >
        <D.Title className="sr-only">{title}</D.Title>
        <D.Description className="sr-only">{title}</D.Description>
        {children}
      </D.Content>
    </D.Portal>
  );
}

// ---------- Sheet (side panel) ----------

export const Sheet = D.Root;
export const SheetTrigger = D.Trigger;
export const SheetClose = D.Close;

export function SheetContent({
  className,
  children,
  side = 'right',
  title,
  ...props
}: ComponentProps<typeof D.Content> & { side?: 'right' | 'left'; title: string }) {
  return (
    <D.Portal>
      <D.Overlay className="fixed inset-0 z-50 animate-fade bg-overlay" />
      <D.Content
        className={cn(
          'fixed top-0 z-50 flex h-full w-[min(100vw,44rem)] flex-col border-border bg-surface shadow-pop',
          side === 'right' ? 'right-0 animate-slide-in border-l' : 'left-0 border-r',
          className,
        )}
        {...props}
      >
        <D.Title className="sr-only">{title}</D.Title>
        <D.Description className="sr-only">{title}</D.Description>
        {children}
      </D.Content>
    </D.Portal>
  );
}

// ---------- Popover ----------

export const Popover = P.Root;
export const PopoverTrigger = P.Trigger;
export const PopoverAnchor = P.Anchor;
export const PopoverClose = P.Close;

export function PopoverContent({
  className,
  align = 'start',
  sideOffset = 6,
  ...props
}: ComponentProps<typeof P.Content>) {
  return (
    <P.Portal>
      <P.Content
        align={align}
        sideOffset={sideOffset}
        collisionPadding={8}
        className={cn(
          'z-50 animate-pop rounded-lg border border-border bg-surface p-2 text-sm text-fg shadow-pop outline-none',
          'max-h-(--radix-popover-content-available-height) overflow-y-auto',
          className,
        )}
        {...props}
      />
    </P.Portal>
  );
}

// ---------- Tooltip ----------

export const TooltipProvider = T.Provider;

/** Wrap any element: `<Tooltip label="Search" keys="mod+k"><button/></Tooltip>`. */
export function Tooltip({
  label,
  keys,
  children,
  side = 'bottom',
}: {
  label: ReactNode;
  keys?: string;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
}) {
  if (!label) return <>{children}</>;
  return (
    <T.Root>
      <T.Trigger asChild>{children}</T.Trigger>
      <T.Portal>
        <T.Content
          side={side}
          sideOffset={6}
          collisionPadding={8}
          className="z-[60] flex animate-fade items-center gap-2 rounded-md bg-fg px-2 py-1 text-xs font-medium text-bg shadow-pop"
        >
          {label}
          {keys ? (
            <span className="flex gap-0.5">
              {formatKeys(keys)
                .flat()
                .map((k, i) => (
                  <Kbd key={i} inverted>
                    {k}
                  </Kbd>
                ))}
            </span>
          ) : null}
        </T.Content>
      </T.Portal>
    </T.Root>
  );
}
